import { useEffect, useState } from 'react';

import useSWRInfinite from 'swr/infinite';

// Utils
import { get } from '@/utils/client';

// API Endpoints
import { STORIES } from '@/lib/routes/apiEndpoints';

const getKey = (pageIndex, previousPageData, activeSPID, limit, requestParams) => {
    // No request params
    if (!requestParams) return null;

    // Reached the end
    if (previousPageData && !previousPageData.stories) return null;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0)
        return [
            `${STORIES.replace('%SPID%', activeSPID)}?per_page=${limit}`,
            JSON.stringify(requestParams)
        ];

    // Add the cursor to the API endpoint
    return [
        `${STORIES.replace('%SPID%', activeSPID)}?per_page=${limit}&cursor=${
            previousPageData.cursor
        }`,
        JSON.stringify(requestParams)
    ];
};

const useStories = ({ activeSPID, limit = 20, params = null, options = {} } = {}) => {
    const [requestParams, setRequestParams] = useState(params);
    const [activeSocialProfile, setActiveSocialProfile] = useState(activeSPID);

    useEffect(() => {
        setActiveSocialProfile(activeSPID);
    }, [activeSPID]);

    const {
        data: dataStories,
        size,
        setSize,
        mutate: mutateStories,
        error: storiesError
    } = useSWRInfinite(
        (pageIndex, previousPageData) =>
            getKey(pageIndex, previousPageData, activeSocialProfile, limit, requestParams),
        ([url]) => get(url, requestParams).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    const isLoadingInitialData = !dataStories && !storiesError;
    const isLoadingMore = size > 0 && dataStories && typeof dataStories[size - 1] === 'undefined';

    return {
        dataStories,
        loadMoreStories: () => setSize(size + 1), // Function to load more stories (load more button)
        mutateStories,
        storiesError,
        requestParams,
        setRequestParams, // Setter to change parameters in the url
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore: dataStories && typeof dataStories[dataStories.length - 1].cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};

export default useStories;
